import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { InputHintMessage } from './atoms';

const InputErrorMessageText = styled(InputHintMessage)`
  color: ${colors.textError};
`;

const InputWarningMessageText = styled(InputHintMessage)`
  color: ${colors.textWarning};
`;

type TruncatableMessageProps = {
  message: string;
  shouldTruncate: boolean;
};

const TruncatableMessage = ({
  message,
  shouldTruncate,
}: TruncatableMessageProps) => {
  if (shouldTruncate) {
    return <TruncatedTextPopover>{message}</TruncatedTextPopover>;
  }

  return <>{message}</>;
};

type InputErrorProps = {
  errorId?: string;
  errorMessage: string | undefined | null;
};

type InputWarningProps = {
  warningId?: string;
  warningMessage: string | undefined | null;
};

type InputErrorOrWarningProps = Partial<InputErrorProps> &
  Partial<InputWarningProps> & {
    /**
     * Clip the message to a single line with the full text behind a
     * hover/focus popover. Off by default — field messages should wrap so the
     * reason a field is invalid is always visible. Enable only in tight,
     * fixed-height contexts such as table cells.
     */
    shouldTruncate?: boolean;
  };

/**
 * To make Screen reader announcements reliable, each live-region container
 * must be persistent in the DOM (present before its text populates).
 * If error happens, the Assistive technologies will be able to recognize that
 * there is new content in the container.
 */
const InputErrorMessageContainer = ({
  errorId,
  errorMessage,
  shouldTruncate,
}: InputErrorProps & { shouldTruncate: boolean }) => (
  <div role="alert">
    {errorMessage ? (
      <InputErrorMessageText id={errorId}>
        <TruncatableMessage
          message={errorMessage}
          shouldTruncate={shouldTruncate}
        />
      </InputErrorMessageText>
    ) : null}
  </div>
);

/**
 * To make Screen reader announcements reliable, each live-region container
 * must be persistent in the DOM (present before its text populates).
 * A warning uses role="status" (polite) so it doesn't interrupt the user.
 */
const InputWarningMessageContainer = ({
  warningId,
  warningMessage,
  shouldTruncate,
}: InputWarningProps & { shouldTruncate: boolean }) => (
  <div role="status">
    {warningMessage ? (
      <InputWarningMessageText id={warningId}>
        <TruncatableMessage
          message={warningMessage}
          shouldTruncate={shouldTruncate}
        />
      </InputWarningMessageText>
    ) : null}
  </div>
);

export const InputErrorOrWarning = ({
  errorId,
  errorMessage,
  warningId,
  warningMessage,
  shouldTruncate = false,
}: InputErrorOrWarningProps) => (
  <>
    <InputErrorMessageContainer
      errorId={errorId}
      errorMessage={errorMessage}
      shouldTruncate={shouldTruncate}
    />
    <InputWarningMessageContainer
      warningId={warningId}
      warningMessage={errorMessage ? undefined : warningMessage}
      shouldTruncate={shouldTruncate}
    />
  </>
);
